import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { X, Target, Calendar, Activity, ShieldAlert, Users, FileText } from 'lucide-react';
import type { Person } from './orgData';

interface PersonDrawerProps {
  person: Person;
  onClose: () => void;
}

type DetailTabId =
  | 'mandat'
  | 'roles'
  | 'objectifs'
  | 'kpi'
  | 'guardrails'
  | 'interfaces';

const detailTabs: { id: DetailTabId; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: 'mandat', label: 'Mandat & esprit', icon: FileText },
  { id: 'roles', label: 'Rôles & responsabilités', icon: Users },
  { id: 'objectifs', label: 'Objectifs 12 mois', icon: Calendar },
  { id: 'kpi', label: 'KPI & cadence', icon: Activity },
  { id: 'guardrails', label: 'Guardrails / Risques', icon: ShieldAlert },
  { id: 'interfaces', label: 'Interfaces', icon: Target },
];

export default function PersonDrawer({ person, onClose }: PersonDrawerProps) {
  const [activeTab, setActiveTab] = useState<DetailTabId>('mandat');

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <>
      <motion.div
        className="fixed inset-0 bg-black/70 z-40"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      />
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        initial={{ opacity: 0, scale: 0.96, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 10 }}
        transition={{ type: 'spring', stiffness: 260, damping: 32 }}
        role="dialog"
        aria-modal="true"
        aria-label={`Fiche de ${person.name}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-[90vw] max-w-5xl max-h-[90vh] bg-slate-950 border border-indigo-500/40 shadow-2xl rounded-2xl flex flex-col overflow-hidden">
          <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-indigo-500/40 bg-slate-950/90">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                {person.avatar && (
                  <img
                    src={person.avatar}
                    alt={person.name}
                    className="w-10 h-10 rounded-full border border-indigo-400 object-cover"
                  />
                )}
                <div>
                  <p className="text-sm font-semibold text-white">{person.name}</p>
                  <p className="text-[0.75rem] text-slate-300">{person.title}</p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-1 text-[0.65rem]">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-900/80 border border-slate-600/80 text-slate-200 uppercase tracking-wide">
                  {person.type === 'executive'
                    ? 'Exécutif'
                    : person.type === 'vp'
                    ? 'VP'
                    : person.type === 'director'
                    ? 'Direction'
                    : person.type === 'owner'
                    ? 'Owner PRIsME'
                    : 'Leadership terrain'}
                </span>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-900/80 border border-slate-600/80 text-slate-200">
                  {person.poleLabel}
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/15 text-white transition"
              aria-label="Fermer la fiche individu"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Summary */}
          <div className="px-5 py-3 border-b border-indigo-500/30 bg-slate-950/80">
            <p className="text-xs text-slate-300 mb-2">{person.whyRole}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[0.72rem] text-slate-100">
              <div>
                <p className="font-semibold text-slate-200 mb-1">Mandat</p>
                <ul className="space-y-0.5 list-disc list-inside">
                  {person.summary.mandat.slice(0, 3).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="font-semibold text-slate-200 mb-1">KPI clés</p>
                <ul className="space-y-0.5 list-disc list-inside">
                  {person.summary.kpis.slice(0, 3).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="px-5 pt-3 border-b border-indigo-500/30 bg-slate-950/80">
            <div className="flex gap-1 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-900">
              {detailTabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[0.7rem] whitespace-nowrap transition ${
                      activeTab === tab.id
                        ? 'bg-indigo-500/30 text-white border border-indigo-300/70'
                        : 'bg-slate-900/60 text-slate-200/80 border border-slate-700/80 hover:bg-slate-900'
                    }`}
                  >
                    <Icon className="w-3 h-3" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Deep view content */}
          <div className="flex-1 overflow-y-auto px-5 py-3 text-[0.78rem] text-slate-100 space-y-2">
            {activeTab === 'mandat' && (
              <ul className="space-y-1 list-disc list-inside">
                {person.details.mandatEsprit.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            )}

            {activeTab === 'roles' && (
              <div className="space-y-3">
                <div>
                  <p className="font-semibold text-slate-200 mb-1">Responsible</p>
                  <ul className="space-y-0.5 list-disc list-inside">
                    {person.details.responsibilitiesRaci.responsible.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="font-semibold text-slate-200 mb-1">Accountable</p>
                  <ul className="space-y-0.5 list-disc list-inside">
                    {person.details.responsibilitiesRaci.accountable.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="font-semibold text-slate-200 mb-1">Consulted</p>
                  <ul className="space-y-0.5 list-disc list-inside">
                    {person.details.responsibilitiesRaci.consulted.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {activeTab === 'objectifs' && (
              <ul className="space-y-1 list-disc list-inside">
                {person.details.objectives12Months.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            )}

            {activeTab === 'kpi' && (
              <ul className="space-y-1 list-disc list-inside">
                {person.details.kpisCadence.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            )}

            {activeTab === 'guardrails' && (
              <ul className="space-y-1 list-disc list-inside">
                {person.details.guardrails.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            )}

            {activeTab === 'interfaces' && (
              <ul className="space-y-1 list-disc list-inside">
                {person.details.interfaces.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </motion.div>
    </>
  );
}

